// Line Entry — "Save — review what needs attention" (canvas board 8).
//
// When a save is refused (or warns) on specific LINES, the user sees every
// one at once instead of the first: what must be fixed (with "Go to cell"),
// what to check and confirm (a tick per warning — Save unlocks when every
// one is ticked), and what saving will do. Nothing has been written: the
// header and every line save together.

import { useState } from 'react'
import { AlertCircle, AlertTriangle } from 'lucide-react'
import { Z_CONFIRM } from '../../layers'
import { T, btn, btnPri, btnSm, dialog, dialogFoot, kbd, overlay } from './styles'

export interface ReviewItem {
  key: string
  /** "Line 5" (or "Header"). */
  where: string
  /** The line's identity — its code / name. */
  title: string
  message: string
  path?: Array<{ form_no: string; index: number }>
  field?: string | null
  code?: string | null
}

export interface ReviewSummary {
  label: string
  lines: number
  sums: Array<{ label: string; value: number }>
}

interface Props {
  mustFix: ReviewItem[]
  confirm: ReviewItem[]
  summary: ReviewSummary[]
  recordNoun: string
  onGoTo: (item: ReviewItem) => void
  onBack: () => void
  /** Warnings only: every one ticked → save with them acknowledged. */
  onSave?: () => void
}

export default function SaveReview({ mustFix, confirm, summary, recordNoun, onGoTo, onBack, onSave }: Props) {
  const [ok, setOk] = useState<Set<string>>(() => new Set())
  const blocked = mustFix.length > 0
  const unconfirmed = confirm.filter(c => !ok.has(c.key)).length
  const canSave = !blocked && unconfirmed === 0 && !!onSave
  const heading = blocked
    ? `${mustFix.length} ${mustFix.length === 1 ? 'line' : 'lines'} must be fixed before this ${recordNoun} can be saved`
    : `Check ${confirm.length} ${confirm.length === 1 ? 'line' : 'lines'} before saving this ${recordNoun}`
  const unlock = blocked
    ? `Save unlocks when the ${mustFix.length === 1 ? 'line is' : `${mustFix.length} lines are`} fixed.`
    : unconfirmed ? `Save unlocks when every line is confirmed (${unconfirmed} left).` : 'Ready to save.'

  const row = (it: ReviewItem, action: React.ReactNode) => (
    <div key={it.key} style={{ display: 'grid', gridTemplateColumns: '70px minmax(0,1fr) auto', gap: 12,
      alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #edf0f4', fontSize: 14 }}>
      <span style={{ fontFamily: T.mono, color: T.muted }}>{it.where}</span>
      <span>
        {it.title && <><b style={{ fontFamily: T.mono }}>{it.title}</b><br /></>}
        <span style={{ color: action === null ? T.muted : undefined }}>{it.message}</span>
      </span>
      {action}
    </div>
  )

  return (
    <div role="presentation" style={{ ...overlay, zIndex: Z_CONFIRM, paddingTop: 48 }}>
      <div role="dialog" aria-modal="true" aria-labelledby="le-review-title" style={{ ...dialog, maxWidth: 1040 }}>
        <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${T.border}` }}>
          <h2 id="le-review-title" style={{ margin: '0 0 4px', fontSize: 19, fontWeight: 600 }}>{heading}</h2>
          <p style={{ margin: 0, fontSize: 14, color: T.muted }}>Nothing has been saved yet.</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', overflow: 'auto', flex: 1 }}>
          <div style={{ padding: '4px 24px 20px', borderRight: `1px solid #edf0f4` }}>
            {blocked && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600, margin: '18px 0 2px', color: '#a61b1b' }}>
                  <AlertCircle size={16} aria-hidden="true" /> Must fix · {mustFix.length}
                </div>
                {mustFix.map(it => row(it,
                  <button type="button" style={btnSm} onClick={() => onGoTo(it)}>Go to cell</button>))}
              </>
            )}
            {confirm.length > 0 && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600, margin: '18px 0 2px', color: '#9a4a00' }}>
                  <AlertTriangle size={16} aria-hidden="true" /> Check and confirm · {confirm.length}
                </div>
                {confirm.map(it => row(it,
                  <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
                    <input type="checkbox" checked={ok.has(it.key)} disabled={blocked}
                      onChange={() => setOk(prev => {
                        const n = new Set(prev)
                        if (n.has(it.key)) n.delete(it.key)
                        else n.add(it.key)
                        return n
                      })} /> OK
                  </label>))}
              </>
            )}
          </div>
          <aside aria-label="What saving will do" style={{ padding: '20px 24px', background: T.rowAlt, display: 'flex',
            flexDirection: 'column', gap: 14, fontSize: 14 }}>
            <b style={{ fontSize: 15 }}>When you save</b>
            {summary.map(s => (
              <div key={s.label} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ color: T.muted }}>{s.label}</span>
                <span>
                  <b>{s.lines.toLocaleString()} {s.lines === 1 ? 'line' : 'lines'}</b>
                  {s.sums.map(x => <span key={x.label}> · <b style={{ fontFamily: T.mono }}>{x.value.toLocaleString()}</b> {x.label.toLowerCase()}</span>)}
                </span>
              </div>
            ))}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ color: T.muted }}>All or nothing</span>
              <span>The header and every line save together. If anything fails, nothing is written.</span>
            </div>
          </aside>
        </div>
        <div style={dialogFoot}>
          <span style={{ fontSize: 14, color: T.muted }}>{unlock}</span>
          <span style={{ flex: 1 }} />
          <button type="button" style={btn} onClick={onBack}>Back to lines</button>
          {blocked && (
            <button type="button" style={btn} onClick={() => onGoTo(mustFix[0])}>
              Fix next <span style={kbd}>Alt ↓</span>
            </button>
          )}
          <button type="button" disabled={!canSave} onClick={onSave}
            style={{ ...(canSave ? btnPri : btn), opacity: canSave ? 1 : 0.6, cursor: canSave ? 'pointer' : 'not-allowed' }}>
            Save {recordNoun}
          </button>
        </div>
      </div>
    </div>
  )
}
